import React, { useMemo } from "react";
import { useLang } from "@/contexts/LangContext";
import MotionReveal from "@/components/MotionReveal";
import { usePhotoLayouts, pickPhotos } from "@/hooks/usePhotoLayouts";
import StoryChaptersTOC from "@/components/StoryChaptersTOC";
import {
  STORY_CHAPTER_DEFAULTS as IMGS,
  STORY_VENUE_DEFAULTS as VENUE_IMGS,
} from "@/data/defaultPhotoLayouts";

// Curated photo album — every image is intentionally chosen and hand-mapped
// to the chapter it belongs to. Order matters: story flows chronologically.
// Defaults live in /data/defaultPhotoLayouts.js so the admin CMS can preview
// and edit them from a single source of truth.

// Photos render at their NATURAL aspect ratio — no borders, no backgrounds, no cropping.
// A soft radius + gentle shadow keeps the editorial feel without framing the image.
const Photo = ({ src }) => (
  <img
    src={src}
    alt=""
    loading="lazy"
    className="block w-full h-auto rounded-md shadow-[0_10px_30px_rgba(31,31,31,0.08)]"
  />
);

const PhotoGrid = ({ photos }) => {
  const n = photos.length;
  if (n === 0) return null;

  if (n === 1) {
    return (
      <div className="mx-auto max-w-[720px]">
        <Photo src={photos[0]} />
      </div>
    );
  }

  if (n === 2) {
    // Two photos side by side — allow natural heights via flex-start alignment
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 items-start">
        {photos.map((p) => <Photo key={p} src={p} />)}
      </div>
    );
  }

  // 3+ photos: masonry-style CSS columns preserve natural aspect ratios
  // and pack images tightly with no letterboxing or face cropping.
  const cols =
    n <= 3
      ? "columns-1 sm:columns-2 md:columns-3"
      : n <= 6
      ? "columns-1 sm:columns-2 lg:columns-3"
      : "columns-1 sm:columns-2 lg:columns-3 xl:columns-4";

  return (
    <div className={`${cols} gap-4 sm:gap-6 [column-fill:_balance]`}>
      {photos.map((p) => (
        <div key={p} className="mb-4 sm:mb-6 break-inside-avoid">
          <Photo src={p} />
        </div>
      ))}
    </div>
  );
};

const Story = () => {
  const { t, dict } = useLang();
  const { layouts } = usePhotoLayouts();
  const chapterPhotos = useMemo(
    () => IMGS.map((defaults, i) => pickPhotos(layouts, `story.beats.${i}`, defaults)),
    [layouts]
  );
  const venuePhotos = pickPhotos(layouts, "story.venue", VENUE_IMGS);

  return (
    <div data-testid="story-page">
      <StoryChaptersTOC beats={dict.story.beats} />
      <section className="mx-auto max-w-[1180px] px-5 sm:px-8 pt-24 sm:pt-32">
        <MotionReveal>
          <p className="editorial-kicker">{t("story.overline")}</p>
        </MotionReveal>
        <MotionReveal delay={0.05}>
          <h1 className="editorial-h1 mt-6 text-4xl sm:text-6xl lg:text-7xl max-w-[20ch]">
            {t("story.title")}
          </h1>
        </MotionReveal>
        <MotionReveal delay={0.1}>
          <p className="mt-8 text-foreground/75 max-w-[60ch] leading-relaxed text-lg">{t("story.intro")}</p>
        </MotionReveal>
      </section>

      <section className="mx-auto max-w-[1180px] px-5 sm:px-8 mt-20 sm:mt-28" data-testid="story-timeline">
        <ol className="space-y-24 sm:space-y-32">
          {dict.story.beats.map((b, i) => {
            const photos = chapterPhotos[i] || [];
            if (photos.length === 0) {
              return (
                <li
                  key={b.chapter}
                  id={`story-chapter-${i}`}
                  data-chapter-index={i}
                  className="grid gap-10 lg:grid-cols-12 items-end scroll-mt-24"
                >
                  <MotionReveal className="lg:col-span-2 hidden lg:block">
                    <span className="font-serif-display italic text-7xl text-accent/40 select-none">{i + 1}</span>
                  </MotionReveal>
                  <MotionReveal delay={0.05} className="lg:col-span-10">
                    <p className="editorial-kicker text-accent">{b.chapter}</p>
                    <h2 className="editorial-h2 mt-4 text-4xl sm:text-5xl lg:text-6xl max-w-[22ch] font-serif-display italic">
                      {b.title}
                    </h2>
                    <p className="mt-6 text-foreground/75 leading-relaxed max-w-[56ch] text-lg whitespace-pre-line">{b.body}</p>
                  </MotionReveal>
                </li>
              );
            }
            return (
              <li
                key={b.chapter}
                id={`story-chapter-${i}`}
                data-chapter-index={i}
                className="space-y-8 sm:space-y-10 scroll-mt-24"
              >
                <MotionReveal>
                  <div className="grid gap-6 lg:grid-cols-12 items-start">
                    <div className="lg:col-span-1 hidden lg:flex justify-center">
                      <span className="font-serif-display italic text-5xl text-accent/40 select-none leading-none">{i + 1}</span>
                    </div>
                    <div className="lg:col-span-11">
                      <p className="editorial-kicker text-accent">{b.chapter}</p>
                      <h2 className="editorial-h2 mt-3 text-3xl sm:text-5xl max-w-[26ch]">{b.title}</h2>
                      <p className="mt-5 text-foreground/75 leading-relaxed max-w-[64ch] text-lg whitespace-pre-line">{b.body}</p>
                    </div>
                  </div>
                </MotionReveal>
                <MotionReveal delay={0.1}>
                  <PhotoGrid photos={photos} />
                </MotionReveal>
              </li>
            );
          })}
        </ol>
      </section>

      <section className="mx-auto max-w-[1180px] px-5 sm:px-8 mt-24 sm:mt-32" data-testid="story-venue">
        <MotionReveal>
          <p className="editorial-kicker text-accent text-center">— The place —</p>
        </MotionReveal>
        <MotionReveal delay={0.05}>
          <h2 className="editorial-h2 mt-4 text-3xl sm:text-5xl max-w-[22ch] mx-auto text-center font-serif-display italic">
            Ammos Kalambouri, Cyprus.
          </h2>
        </MotionReveal>
        <MotionReveal delay={0.1}>
          <div className="mt-12 grid gap-5 sm:gap-6 md:grid-cols-2">
            {venuePhotos.map((src, i) => (
              <img
                key={src}
                src={src}
                alt="Wedding venue in Cyprus"
                loading="lazy"
                className={`block w-full h-auto rounded-md shadow-[0_14px_40px_rgba(31,31,31,0.10)] ${
                  i === 0 ? "md:col-span-2" : ""
                }`}
              />
            ))}
          </div>
        </MotionReveal>
      </section>

      <section className="mx-auto max-w-[1180px] px-5 sm:px-8 py-28 sm:py-36 text-center">
        <MotionReveal>
          <p className="editorial-kicker text-accent">{t("story.closingOverline") || "— And now —"}</p>
        </MotionReveal>
        <MotionReveal delay={0.05}>
          <p className="font-serif-display italic mt-6 text-3xl sm:text-5xl text-foreground/85 max-w-[24ch] mx-auto leading-tight">
            {t("story.closing")}
          </p>
        </MotionReveal>
      </section>
    </div>
  );
};

export default Story;
